import * as React from 'react';
import {
  Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { formatDuration, type Role } from '@cfl/shared';

import {
  Alert, AlertDescription, Card, CardContent, Skeleton,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/cargofl-ui';
import { currentMonthFilter, FilterBar } from '@/components/reporting/filter-bar';
import { PageContainer } from '@/components/shell/page-container';
import { PageHeader } from '@/components/shell/page-header';
import { useBreakdown, useTotals, type Dimension, type Filters, type TotalRow } from '@/lib/reporting-api';
import type { Session } from '@/lib/session';

const DIMENSIONS: { key: Dimension; label: string; hint: string }[] = [
  { key: 'month', label: 'By month', hint: 'How the hours are spread over time' },
  { key: 'customer', label: 'By customer', hint: 'Which clients the time went to' },
  { key: 'project', label: 'By project', hint: 'Which projects the time went to' },
  { key: 'activity', label: 'By activity', hint: 'What kind of work it was' },
  { key: 'employee', label: 'By person', hint: 'Who recorded it' },
];

/** The dimension tabs, plus the one view that is not a single dimension. */
type Tab = Dimension | 'breakdown';

export function ReportingPage({ session }: { session: Session }) {
  const [filters, setFilters] = React.useState<Filters>(currentMonthFilter);
  const [tab, setTab] = React.useState<Tab>('month');

  return (
    <PageContainer>
      <PageHeader title="Reporting" description="Where the recorded hours went." />
      <FilterBar value={filters} onChange={setFilters} role={session.role} />

      <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
        <TabsList>
          {DIMENSIONS.map((d) => (
            <TabsTrigger key={d.key} value={d.key}>
              {d.label}
            </TabsTrigger>
          ))}
          {/* Each tab above answers ONE level. This one answers how a customer's
              hours split across their projects and the work on them, which is the
              question an invoice conversation starts from and which changing the
              filter to find loses the context of. */}
          <TabsTrigger value="breakdown">Breakdown</TabsTrigger>
        </TabsList>
        {DIMENSIONS.map((d) => (
          <TabsContent key={d.key} value={d.key}>
            {/* Only the visible tab queries. Five dimensions fetched at once is four
                queries nobody asked for, over 24k rows each. */}
            <Report dimension={d.key} hint={d.hint} filters={filters} active={tab === d.key} />
          </TabsContent>
        ))}
        <TabsContent value="breakdown">
          <Breakdown filters={filters} active={tab === 'breakdown'} />
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
}

function Report({
  dimension,
  hint,
  filters,
  active,
}: {
  dimension: Dimension;
  hint: string;
  filters: Filters;
  active: boolean;
}) {
  const { data, isPending, isError, error } = useTotals(dimension, filters, active);

  if (isError) {
    return (
      <Alert className="border-destructive/30">
        <AlertDescription className="text-sm">
          {error instanceof Error ? error.message : 'Could not build that report.'}
        </AlertDescription>
      </Alert>
    );
  }
  if (isPending) return <Skeleton className="h-80 w-full" />;

  const rows = data ?? [];
  const total = rows.reduce((sum, r) => sum + r.seconds, 0);

  if (rows.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-sm text-muted-foreground">Nothing recorded in that range.</p>
        </CardContent>
      </Card>
    );
  }

  // A month report reads left-to-right in time; every other dimension reads as a
  // ranking, so it is sorted by size and the longest bars come first.
  const ordered = dimension === 'month' ? rows : [...rows].sort((a, b) => b.seconds - a.seconds);
  const charted = ordered.slice(0, 12);

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{hint}</p>

      <Card>
        <CardContent className="py-5">
          <Chart rows={charted} horizontal={dimension !== 'month'} />
          {ordered.length > charted.length && (
            <p className="mt-2 text-xs text-muted-foreground">
              Chart shows the top {charted.length}; the table below has all{' '}
              {ordered.length}.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table className="min-w-[460px]">
              <TableHeader>
                <TableRow>
                  <TableHead>{dimension === 'month' ? 'Month' : 'Name'}</TableHead>
                  <TableHead className="text-right">Hours</TableHead>
                  <TableHead className="text-right">Share</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ordered.map((row) => (
                  <TableRow key={row.key}>
                    <TableCell className="font-medium">{row.label}</TableCell>
                    <TableCell className="ts-num text-right">
                      {formatDuration(row.seconds)}
                    </TableCell>
                    <TableCell className="ts-num text-right text-muted-foreground">
                      {total === 0 ? '—' : `${((row.seconds / total) * 100).toFixed(1)}%`}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <tfoot>
                <TableRow className="border-t-2 font-semibold">
                  <TableCell>Total</TableCell>
                  <TableCell className="ts-num text-right">{formatDuration(total)}</TableCell>
                  <TableCell className="ts-num text-right">100%</TableCell>
                </TableRow>
              </tfoot>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * One bar per row, in hours.
 *
 * Hours, not seconds, on the axis: an axis reading "144000" is unreadable and
 * nobody thinks in seconds. Bars are horizontal for anything with a name, because
 * a customer called "CSMC - Chhatrapati Sambhaji Municipal Corporation" cannot be
 * a vertical tick label at any font size.
 *
 * Colours come from the design system's validated categorical palette
 * (--chart-1..5), which is checked as a set for adjacent-hue separation under
 * colour blindness - so the series are cycled through it rather than picked here.
 */
function Chart({ rows, horizontal }: { rows: TotalRow[]; horizontal: boolean }) {
  const data = rows.map((r) => ({
    label: r.label,
    hours: Number((r.seconds / 3600).toFixed(2)),
  }));

  // Height grows with the number of bars in the horizontal layout, so 40 customers
  // do not compress into an unreadable stack.
  const height = horizontal ? Math.max(220, data.length * 34 + 48) : 300;

  // Axis props are computed rather than the axes being wrapped in a conditional
  // fragment. Recharts discovers its Cartesian children by inspecting `children`
  // and does NOT look inside a fragment - wrapping them in `<>...</>` produced a
  // chart with no axes and every bar stacked at the origin, which is what the first
  // version of this did.
  const hours = (v: number) => `${v}h`;
  const truncate = (v: string) => (v.length > 30 ? `${v.slice(0, 29)}\u2026` : v);
  const tick = { fill: 'currentColor', fontSize: 11 } as const;

  return (
    // `text-muted-foreground` on the wrapper, `currentColor` on every axis: recharts
    // writes tick colours into SVG presentation attributes, where a
    // `hsl(var(--token))` value did not resolve.
    <div className="text-muted-foreground">
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={data}
          layout={horizontal ? 'vertical' : 'horizontal'}
          margin={{ top: 8, right: 32, bottom: 8, left: 8 }}
        >
          <CartesianGrid
            stroke="currentColor"
            strokeOpacity={0.18}
            strokeDasharray="3 3"
            horizontal={!horizontal}
            vertical={horizontal}
          />
          <XAxis
            {...(horizontal
              ? { type: 'number' as const, tickFormatter: hours }
              : { type: 'category' as const, dataKey: 'label' })}
            tick={tick}
            stroke="currentColor"
            strokeOpacity={0.3}
          />
          <YAxis
            {...(horizontal
              ? {
                  type: 'category' as const,
                  dataKey: 'label',
                  width: 200,
                  // Truncated rather than wrapped: a wrapped label pushes the bars
                  // out of line with their own row.
                  tickFormatter: truncate,
                }
              : { type: 'number' as const, tickFormatter: hours })}
            tick={tick}
            stroke="currentColor"
            strokeOpacity={0.3}
          />
          <Tooltip
            cursor={{ fill: 'currentColor', fillOpacity: 0.06 }}
            // Needed as well as `formatter`: without it the tooltip titled itself
            // with the row index rather than the name.
            labelFormatter={(_l, payload) => payload?.[0]?.payload?.label ?? ''}
            formatter={(v: number) => [formatDuration(v * 3600), 'Hours']}
            contentStyle={{
              background: 'hsl(var(--popover))',
              border: '1px solid hsl(var(--border))',
              borderRadius: 8,
              color: 'hsl(var(--popover-foreground))',
              fontSize: 12,
            }}
          />
          <Bar
            dataKey="hours"
            // Capped, or a single category stretches its bar across the whole plot
            // and stops reading as a bar at all.
            maxBarSize={horizontal ? 22 : 56}
            radius={horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0]}
          >
            {data.map((_, i) => (
              <Cell key={i} fill={`hsl(var(--chart-${(i % 5) + 1}))`} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Customer, then its projects, then the activities on them.
 *
 * Every level is open. A breakdown that starts collapsed makes somebody click
 * through three levels to learn what the page is for, and the whole point is to
 * see the shape at once; the filter bar above is what narrows it when it is long.
 */
function Breakdown({ filters, active }: { filters: Filters; active: boolean }) {
  const { data, isPending } = useBreakdown(filters, active);

  if (!active) return null;
  if (isPending) return <Skeleton className="mt-4 h-64 w-full" />;
  if (!data || data.length === 0) {
    return (
      <Alert className="mt-4">
        <AlertDescription className="text-sm">
          No hours match this filter, so there is nothing to break down.
        </AlertDescription>
      </Alert>
    );
  }

  const total = data.reduce((sum, c) => sum + c.seconds, 0);

  return (
    <div className="mt-4 space-y-3">
      <p className="text-sm text-muted-foreground">
        {formatDuration(total)} across {data.length}{' '}
        {data.length === 1 ? 'customer' : 'customers'}.
      </p>

      <div className="space-y-2">
        {data.map((customer) => (
          <div key={customer.id} className="rounded-lg border">
            <div className="flex items-baseline justify-between gap-3 border-b bg-muted/50 px-4 py-2">
              <span className="font-semibold">{customer.name}</span>
              <span className="ts-num text-sm font-semibold tabular-nums">
                {formatDuration(customer.seconds)}
              </span>
            </div>
            <div className="divide-y">
              {customer.projects.map((project) => (
                <div key={project.id} className="px-4 py-2">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-sm font-medium">{project.name}</span>
                    <span className="ts-num text-sm tabular-nums">
                      {formatDuration(project.seconds)}
                    </span>
                  </div>
                  <ul className="mt-1 space-y-0.5">
                    {project.activities.map((activity) => (
                      <li
                        key={activity.id}
                        className="flex items-baseline justify-between gap-3 pl-4 text-xs text-muted-foreground"
                      >
                        <span className="truncate">{activity.name}</span>
                        <span className="ts-num shrink-0 tabular-nums">
                          {formatDuration(activity.seconds)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
